import { useEffect, useRef, useState, useCallback, createContext, useContext } from "react";
import {
    Box,
    Fab,
    Paper,
    Typography,
    TextField,
    IconButton,
    Button,
    Chip,
    Slide,
    Divider,
    Tooltip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Avatar,
} from "@mui/material";
import CloseIcon               from "@mui/icons-material/Close";
import SendIcon                from "@mui/icons-material/Send";
import RestartAltIcon          from "@mui/icons-material/RestartAlt";
import PersonIcon              from "@mui/icons-material/Person";
import ChatIcon                from "@mui/icons-material/Chat";
import OpenWithIcon            from "@mui/icons-material/OpenWith";
import FullscreenIcon          from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon      from "@mui/icons-material/FullscreenExit";
import AutoAwesomeIcon         from "@mui/icons-material/AutoAwesome";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    ResponsiveContainer, LineChart, Line, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
} from "recharts";
import useChat from "@plannel/components/useChat";
import { FS } from "@plannel/utils/font-util";

const MIN_WIDTH  = 360;
const MAX_WIDTH  = 960;
const MIN_HEIGHT = 480;
const MAX_HEIGHT = 960;

// ── Fullscreen Context ───────────────────────────────────────────────────────
const FullscreenCtx = createContext(false);

// ── 차트 색상 팔레트 ────────────────────────────────────────────────────────
const CHART_COLORS = ["#1565c0","#2e7d32","#e65100","#6a1b9a","#c62828","#00838f","#558b2f","#4527a0"];

// ── 차트 블록 렌더러 ─────────────────────────────────────────────────────────
const ChatChart = ({ raw }) => {
    const fullscreen = useContext(FullscreenCtx);
    const chartHeight = fullscreen ? 380 : 230;

    let spec;
    try { spec = JSON.parse(raw); }
    catch { return <Box component="pre" sx={{ fontSize: FS.SUB, whiteSpace: "pre-wrap", p: 1, bgcolor: "grey.100", borderRadius: 1 }}>{raw}</Box>; }

    const { type = "line", title, data = [], xKey, series = [] } = spec;
    if (!data.length) return null;

    const autoX      = xKey || Object.keys(data[0])[0];
    const autoSeries = series.length > 0
        ? series
        : Object.keys(data[0]).filter(k => k !== autoX).map(k => ({ key: k, name: k }));

    const ChartComp  = type === "bar" ? BarChart  : LineChart;
    const SeriesComp = type === "bar" ? Bar       : Line;

    const tickFontSize  = fullscreen ? FS.SUB : FS.CAPTION;
    const legendFontSize = fullscreen ? FS.TITLE : FS.BODY;

    return (
        <Box sx={{ width: "100%", mt: 1.5, mb: 1 }}>
            {title && (
                <Typography variant="caption" sx={{ display: "block", textAlign: "center", mb: 0.5, color: "text.secondary", fontWeight: 600, fontSize: fullscreen ? 13 : 11 }}>
                    {title}
                </Typography>
            )}
            <ResponsiveContainer width="100%" height={chartHeight}>
                <ChartComp data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey={autoX} tick={{ fontSize: tickFontSize }} tickLine={false} />
                    <YAxis tick={{ fontSize: tickFontSize }} width={fullscreen ? 64 : 52} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                    <RechartsTooltip
                        contentStyle={{ fontSize: fullscreen ? FS.TITLE : FS.SUB, borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
                        formatter={v => v?.toLocaleString()}
                    />
                    <Legend wrapperStyle={{ fontSize: legendFontSize, paddingTop: 6 }} />
                    {autoSeries.map((s, idx) => (
                        <SeriesComp
                            key={s.key}
                            dataKey={s.key}
                            name={s.name || s.key}
                            stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                            fill={CHART_COLORS[idx % CHART_COLORS.length]}
                            dot={false}
                            strokeWidth={s.name === "실적" ? 2.5 : 1.8}
                            connectNulls={false}
                        />
                    ))}
                </ChartComp>
            </ResponsiveContainer>
        </Box>
    );
};

// ── 마크다운 커스텀 컴포넌트 ────────────────────────────────────────────────
const buildMdComponents = (fullscreen) => {
    const fs = {
        body:  fullscreen ? FS.H1 : FS.TITLE,
        small: fullscreen ? FS.TITLE : FS.SUB,
        h1:    fullscreen ? FS.H1 : FS.H2,
        h2:    fullscreen ? FS.H1 : FS.TITLE,
        h3:    fullscreen ? FS.H2 : FS.SUB,
        li:    fullscreen ? FS.H1 : FS.TITLE,
    };
    return {
    code({ inline, className, children }) {
            const raw = String(children).replace(/\n$/, "");
            if (!inline && className === "language-chart") return <ChatChart raw={raw} />;
            if (!inline) return (
                <Box component="pre" sx={{ bgcolor: "#f5f7fa", borderRadius: 1.5, p: 1.2, fontSize: fs.small, overflowX: "auto", whiteSpace: "pre-wrap", fontFamily: "monospace", border: "1px solid #e8eaed" }}>
                    {raw}
                </Box>
            );
            return (
                <Box component="code" sx={{ bgcolor: "#f0f4ff", color: "#1565c0", borderRadius: 0.5, px: 0.6, fontSize: "0.85em", fontFamily: "monospace" }}>
                    {children}
                </Box>
            );
        },
        table({ children }) {
            return (
                <Box sx={{ overflowX: "auto", my: 1.2, borderRadius: 1.5, border: "1px solid #e0e7ff", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                    <Table size="small" sx={{ "& th, & td": { fontSize: fs.small, py: fullscreen ? 1 : 0.7, px: fullscreen ? 1.8 : 1.2 }, "& tr:nth-of-type(even) td": { bgcolor: "#f8faff" } }}>
                        {children}
                    </Table>
                </Box>
            );
        },
        thead({ children }) { return <TableHead sx={{ bgcolor: "#1565c0" }}>{children}</TableHead>; },
        tbody({ children }) { return <TableBody>{children}</TableBody>; },
        tr({ children })    { return <TableRow hover>{children}</TableRow>; },
        th({ children })    { return <TableCell sx={{ fontWeight: 700, color: "#fff", fontSize: fs.small, whiteSpace: "nowrap", borderBottom: "none" }}>{children}</TableCell>; },
        td({ children })    { return <TableCell sx={{ fontSize: fs.small, borderBottom: "1px solid #eef0f8" }}>{children}</TableCell>; },
        h1({ children }) {
            return (
                <Box sx={{ background: "linear-gradient(90deg,#1565c0,#1976d2)", color: "#fff", borderRadius: 1.5, px: 1.5, py: 0.7, mt: 1.5, mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: "inherit", fontSize: fs.h1 }}>{children}</Typography>
                </Box>
            );
        },
        h2({ children }) {
            return (
                <Box sx={{ borderLeft: "3px solid #1565c0", pl: 1.2, mt: 1.2, mb: 0.6, bgcolor: "#f0f4ff", borderRadius: "0 8px 8px 0", py: 0.4 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: fs.h2 }}>{children}</Typography>
                </Box>
            );
        },
        h3({ children }) {
            return <Typography variant="body2" fontWeight={700} sx={{ mt: 0.8, mb: 0.3, color: "#1565c0", fontSize: fs.h3 }}>{children}</Typography>;
        },
        p({ children })  { return <Typography variant="body2" sx={{ mb: 0.7, lineHeight: 1.8, fontSize: fs.body, color: "#2d3748" }}>{children}</Typography>; },
        ul({ children }) { return <Box component="ul" sx={{ pl: 2.2, mb: 0.8, mt: 0.2, "& li": { fontSize: fs.li, lineHeight: 1.75, mb: 0.25, color: "#2d3748" } }}>{children}</Box>; },
        ol({ children }) { return <Box component="ol" sx={{ pl: 2.2, mb: 0.8, mt: 0.2, "& li": { fontSize: fs.li, lineHeight: 1.75, mb: 0.25, color: "#2d3748" } }}>{children}</Box>; },
        hr()             { return <Divider sx={{ my: 1.2, borderColor: "#e8eaed" }} />; },
        strong({ children }) { return <Box component="strong" sx={{ fontWeight: 700, color: "#1a202c" }}>{children}</Box>; },
        em({ children })     { return <Box component="em" sx={{ fontStyle: "italic", color: "#4a5568" }}>{children}</Box>; },
        sub({ children })    { return <>{children}</>; },
        del({ children })    { return <>{children}</>; },
        blockquote({ children }) {
            return (
                <Box sx={{ borderLeft: "3px solid #f59e0b", bgcolor: "#fffbeb", pl: 1.5, py: 0.6, my: 0.8, borderRadius: "0 8px 8px 0" }}>
                    {children}
                </Box>
            );
        },
    };
};

// ── 점 애니메이션 (분석 중) ──────────────────────────────────────────────────
const TypingDots = () => (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", py: 0.5 }}>
        {[0, 1, 2].map(i => (
            <Box key={i} sx={{
                width: 6, height: 6, borderRadius: "50%", bgcolor: "#1565c0",
                animation: "bounce 1.2s infinite",
                animationDelay: `${i * 0.2}s`,
                "@keyframes bounce": { "0%,80%,100%": { transform: "scale(0.6)", opacity: 0.4 }, "40%": { transform: "scale(1)", opacity: 1 } },
            }} />
        ))}
    </Box>
);

const ChatWidget = ({ pageId, autoAnalysisPrompt, pageContext, autoAnalysisTrigger }) => {
    const [open, setOpen]             = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [input, setInput]           = useState("");
    const [autoAnalysisMode, setAutoAnalysisMode] = useState(false);
    const [panelSize, setPanelSize]   = useState({ width: 420, height: 560 });
    const panelSizeRef = useRef({ width: 420, height: 560 });
    const bottomRef    = useRef(null);

    // fullscreen 변경 시 mdComponents 재생성
    const mdComponents = buildMdComponents(fullscreen);

    const { messages, loading, error, sendMessage, reset } = useChat(pageId);

    useEffect(() => { panelSizeRef.current = panelSize; }, [panelSize]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

    useEffect(() => {
        if (!autoAnalysisTrigger || !autoAnalysisPrompt) return;
        setOpen(true);
        setAutoAnalysisMode(true);
        sendMessage(autoAnalysisPrompt, pageContext, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoAnalysisTrigger]);

    // ── FAB 클릭 시 자동 분석 실행 (최초 열기 + 메시지 없을 때) ──────────────
    useEffect(() => {
        if (open && autoAnalysisPrompt && messages.length === 0 && !loading) {
            setAutoAnalysisMode(true);
            sendMessage(autoAnalysisPrompt, pageContext, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;
        sendMessage(input.trim(), pageContext);
        setInput("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
    };

    const handleResizeMouseDown = useCallback((e) => {
        if (fullscreen) return;
        e.preventDefault(); e.stopPropagation();
        const startX = e.clientX, startY = e.clientY;
        const startW = panelSizeRef.current.width, startH = panelSizeRef.current.height;
        const onMove = (ev) => {
            setPanelSize({
                width:  Math.max(MIN_WIDTH,  Math.min(MAX_WIDTH,  startW + (startX - ev.clientX))),
                height: Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, startH + (startY - ev.clientY))),
            });
        };
        const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            document.body.style.cursor = document.body.style.userSelect = "";
        };
        document.body.style.cursor = "nw-resize";
        document.body.style.userSelect = "none";
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    }, [fullscreen]);

    // ── 패널 위치/크기 스타일 ──────────────────────────────────────────────
    const panelSx = fullscreen
        ? { position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", borderRadius: 0, zIndex: 1400 }
        : { position: "fixed", bottom: 24, right: 24, width: panelSize.width, height: panelSize.height, borderRadius: "16px", zIndex: 1300 };

    return (
        <FullscreenCtx.Provider value={fullscreen}>
        <>
            {/* FAB */}
            {!open && (
                <Tooltip title="PlanNEL AI 분석" placement="left" arrow>
                    <Fab
                        onClick={() => setOpen(true)}
                        sx={{
                            position: "fixed", bottom: 24, right: 24, zIndex: 1300,
                            background: "linear-gradient(135deg, #1565c0, #42a5f5)",
                            color: "#fff", boxShadow: "0 4px 20px rgba(21,101,192,0.45)",
                            "&:hover": { background: "linear-gradient(135deg, #0d47a1, #1e88e5)" },
                        }}
                    >
                        <AutoAwesomeIcon />
                    </Fab>
                </Tooltip>
            )}

            {/* 채팅 패널 */}
            <Slide direction="up" in={open} mountOnEnter unmountOnExit>
                <Paper elevation={0} sx={{
                    ...panelSx,
                    display: "flex", flexDirection: "column", overflow: "hidden",
                    border: fullscreen ? "none" : "1px solid rgba(21,101,192,0.15)",
                    boxShadow: fullscreen ? "none" : "0 12px 48px rgba(0,0,0,0.18)",
                    transition: fullscreen ? "none" : "none",
                }}>
                    {/* 리사이즈 핸들 */}
                    {!fullscreen && (
                        <Tooltip title="드래그하여 크기 조정" placement="right" arrow>
                            <Box onMouseDown={handleResizeMouseDown} sx={{
                                position: "absolute", top: 0, left: 0, width: 22, height: 22,
                                cursor: "nw-resize", zIndex: 10,
                                display: "flex", alignItems: "flex-start", justifyContent: "flex-start",
                                color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff" },
                            }}>
                                <OpenWithIcon sx={{ fontSize: 13, mt: "4px", ml: "4px" }} />
                            </Box>
                        </Tooltip>
                    )}

                    {/* 헤더 */}
                    <Box sx={{
                        display: "flex", alignItems: "center", gap: 1,
                        px: 2, py: 1,
                        background: "linear-gradient(90deg, #1565c0 0%, #1976d2 100%)",
                        color: "#fff", flexShrink: 0,
                    }}>
                        <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, fontSize: FS.H2, letterSpacing: 0.3 }}>
                            PlanNEL AI
                        </Typography>
                        {pageId && (
                            <Chip label={pageId} size="small" sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontSize: FS.CAPTION, height: 18, border: "1px solid rgba(255,255,255,0.3)" }} />
                        )}
                        {autoAnalysisMode && (
                            <Chip label="자동분석" size="small" sx={{ bgcolor: "#43a047", color: "#fff", fontSize: FS.CAPTION, height: 18 }} />
                        )}
                        <Tooltip title={fullscreen ? "창 모드" : "전체 화면"} arrow>
                            <IconButton size="small" onClick={() => setFullscreen(f => !f)} sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff" } }}>
                                {fullscreen ? <FullscreenExitIcon sx={{ fontSize: 17 }} /> : <FullscreenIcon sx={{ fontSize: 17 }} />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="대화 초기화" arrow>
                            <IconButton size="small" onClick={reset} sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff" } }}>
                                <RestartAltIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="닫기" arrow>
                            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "rgba(255,255,255,0.8)", "&:hover": { color: "#fff" } }}>
                                <CloseIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* 메시지 영역 */}
                    <Box sx={{
                        flex: 1, overflowY: "auto", px: fullscreen ? 6 : 2, py: 2,
                        display: "flex", flexDirection: "column", gap: 1.5,
                        bgcolor: "#f7f9fc",
                        maxWidth: fullscreen ? 1100 : "100%",
                        mx: "auto", width: "100%",
                        "&::-webkit-scrollbar": { width: 4 },
                        "&::-webkit-scrollbar-thumb": { bgcolor: "#c0cfe0", borderRadius: 4 },
                    }}>
                        {messages.length === 0 && (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 6, gap: 1 }}>
                                <AutoAwesomeIcon sx={{ fontSize: 40, color: "#c5d8f5" }} />
                                <Typography variant="body2" color="text.disabled" align="center">
                                    {pageId ? `${pageId} 화면에 대해 질문해보세요.` : "PlanNEL 데이터에 대해 무엇이든 질문해보세요."}
                                </Typography>
                            </Box>
                        )}

                        {messages.map((msg, i) => {
                            if (msg.hidden) return null;
                            const isUser = msg.role === "user";
                            return (
                                <Box key={i} sx={{ display: "flex", gap: 1, alignSelf: isUser ? "flex-end" : "flex-start", maxWidth: fullscreen ? "75%" : "92%" }}>
                                    {!isUser && (
                                        <Avatar sx={{ width: 28, height: 28, bgcolor: "#1565c0", flexShrink: 0, mt: 0.3 }}>
                                            <AutoAwesomeIcon sx={{ fontSize: 15 }} />
                                        </Avatar>
                                    )}
                                    <Box sx={{
                                        px: 1.5, py: 1.2,
                                        borderRadius: isUser ? "16px 16px 4px 16px" : "4px 16px 16px 16px",
                                        bgcolor: isUser ? "#1565c0" : "#fff",
                                        color: isUser ? "#fff" : "text.primary",
                                        boxShadow: isUser ? "0 2px 8px rgba(21,101,192,0.25)" : "0 2px 8px rgba(0,0,0,0.08)",
                                        border: isUser ? "none" : "1px solid #eef0f8",
                                        "& p:last-child": { mb: 0 },
                                    }}>
                                        {isUser ? (
                                            <Typography variant="body2" sx={{ fontSize: fullscreen ? 15 : 13, lineHeight: 1.6, whiteSpace: "pre-wrap", color: "inherit" }}>
                                                {msg.content}
                                            </Typography>
                                        ) : (
                                            <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]} components={mdComponents}>
                                                {msg.content}
                                            </ReactMarkdown>
                                        )}
                                    </Box>
                                    {isUser && (
                                        <Avatar sx={{ width: 28, height: 28, bgcolor: "#90a4ae", flexShrink: 0, mt: 0.3 }}>
                                            <PersonIcon sx={{ fontSize: 16 }} />
                                        </Avatar>
                                    )}
                                </Box>
                            );
                        })}

                        {loading && (
                            <Box sx={{ display: "flex", gap: 1, alignSelf: "flex-start", alignItems: "center" }}>
                                <Avatar sx={{ width: 28, height: 28, bgcolor: "#1565c0" }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 15 }} />
                                </Avatar>
                                <Box sx={{ px: 1.5, py: 1, bgcolor: "#fff", borderRadius: "4px 16px 16px 16px", border: "1px solid #eef0f8", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                                    <TypingDots />
                                </Box>
                            </Box>
                        )}

                        {error && (
                            <Box sx={{ alignSelf: "center", bgcolor: "#fff3e0", border: "1px solid #ffb74d", borderRadius: 2, px: 2, py: 1 }}>
                                <Typography variant="caption" color="error">{error}</Typography>
                            </Box>
                        )}
                        <div ref={bottomRef} />
                    </Box>

                    <Divider sx={{ borderColor: "#e8edf5" }} />

                    {/* 입력 영역 */}
                    <Box sx={{ bgcolor: "#fff", px: fullscreen ? 6 : 1.5, py: 1.2, flexShrink: 0, maxWidth: fullscreen ? 1100 : "100%", mx: "auto", width: "100%" }}>
                        {autoAnalysisMode ? (
                            <Button
                                startIcon={<ChatIcon fontSize="small" />}
                                onClick={() => setAutoAnalysisMode(false)}
                                size="small" variant="outlined" color="primary" fullWidth
                                disabled={loading}
                                sx={{ borderRadius: 3, textTransform: "none", fontWeight: 600 }}
                            >
                                직접 질문하기
                            </Button>
                        ) : (
                            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                                <TextField
                                    value={input}
                                    onChange={e => setInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="질문을 입력하세요..."
                                    disabled={loading}
                                    multiline maxRows={4} size="small" fullWidth variant="outlined"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            fontSize: FS.TITLE, borderRadius: 3,
                                            bgcolor: "#f7f9fc",
                                            "& fieldset": { borderColor: "#dde3f0" },
                                            "&:hover fieldset": { borderColor: "#90afd4" },
                                            "&.Mui-focused fieldset": { borderColor: "#1565c0" },
                                        },
                                    }}
                                />
                                <IconButton type="submit" disabled={loading || !input.trim()} sx={{
                                    bgcolor: "#1565c0", color: "#fff", borderRadius: 2,
                                    width: 38, height: 38, flexShrink: 0,
                                    "&:hover": { bgcolor: "#0d47a1" },
                                    "&.Mui-disabled": { bgcolor: "#e0e7ff", color: "#90a4ae" },
                                }}>
                                    <SendIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Slide>
        </>
        </FullscreenCtx.Provider>
    );
};

export default ChatWidget;

